export interface Client {
  id: string;
  name: string;
  country: string;
  lastUpdated?: string;
  logo?: string;
  shortDescription?: string;
  orderIndex?: number;
}

export interface Provider {
  id: string;
  name: string;
  url?: string;
}

export interface Contact {
  id: string;
  name: string;
  role: string;
  email?: string;
  phone?: string;
  providerId: string;
  provider?: Provider;
}

export interface ProjectPhase {
  current: number;
  total: number;
  name: string;
  description: string;
  targetDate: string;
  progress: number;
  milestone?: string;
}

export interface ProjectPhaseDetail {
  id: string;
  phaseNumber: number;
  name: string;
  description: string;
  targetDate: string;
  progress: number;
  milestone?: string;
  status: 'completed' | 'in_progress' | 'pending';
}

export interface EditableProjectPhaseDetail extends ProjectPhaseDetail {
  clientId?: string;
  order_index?: number;
  isNew?: boolean;
  isDeleted?: boolean;
}

export interface ProjectScale {
  projectSize: string;
}

export interface ClientOverview {
  client: Client;
  provider: Provider;
  openAITools: string;
  useCase: string;
  startDate: string;
  currentPhase: string;
  projectScale: ProjectScale;
  ultimateGoal?: string;
  contacts: Contact[];
}

export interface ProjectStatus {
  overallStatus: string;
  currentPhase: ProjectPhase;
  allPhases?: ProjectPhaseDetail[];
}

export interface InsightItem {
  text: string;
  emoji: string;
}

export interface EditableInsightItem extends InsightItem {
  id?: string;
  category: 'key_learnings' | 'opportunities' | 'challenges';
  title?: string;
  order_index?: number;
  isNew?: boolean;
  isDeleted?: boolean;
}

export interface InsightTitle {
  id: string;
  clientId: string;
  category: 'key_learnings' | 'opportunities' | 'challenges';
  title: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface GeneralInsights {
  keyLearnings: {
    title: string;
    items: InsightItem[];
  };
  opportunities: {
    title: string;
    items: InsightItem[];
  };
  challenges: {
    title: string;
    items: InsightItem[];
  };
}

export interface ClientVoiceQuote {
  id: string;
  quote: string;
  source: string;
}

export interface TimelineItem {
  id: string;
  date: string;
  title: string;
  description: string;
  emoji: string;
}

export interface EditableTimelineItem extends TimelineItem {
  order_index?: number;
  isNew?: boolean;
  isDeleted?: boolean;
}

export interface DashboardData {
  aiSummary: string;
  clientOverview: ClientOverview;
  projectStatus: ProjectStatus;
  generalInsights: GeneralInsights;
  clientVoice: ClientVoiceQuote[];
  timeline: TimelineItem[];
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// User and Authentication interfaces
export interface Role {
  id: string;
  name: string;
  description?: string;
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  auth0Id: string;
  email: string;
  name: string;
  picture?: string;
  role: Role;
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  EDITOR = 'editor',
  VIEWER = 'viewer'
}

// Edit-specific interfaces
export interface UpdateClientOverviewRequest {
  client?: Partial<Omit<Client, 'id' | 'lastUpdated'>>;
  providerId?: string;
  openAITools?: string;
  useCase?: string;
  startDate?: string;
  currentPhase?: string;
  projectScale?: Partial<ProjectScale>;
  ultimateGoal?: string;
  contacts?: {
    toAdd?: (Omit<Contact, 'id'> & { providerId: string })[];
    toUpdate?: { id: string; data: Partial<Omit<Contact, 'id'>> }[];
    toDelete?: string[];
  };
}

export interface SubmitQuestionRequest {
  clientId: string;
  question: string;
  userEmail?: string;
  userName?: string;
} 